import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Button, Stack, IconButton, Paper} from 'views/components';
import {Edit as EditIcon, MedicalInformationOutlined as MedicalInformationOutlinedIcon} from 'views/icons';
import {MarkdownViewer, MarkdownEditor, Translate, translate} from 'views/module';

const MedicalHistory = (props) => {
  const {medicalHistory, onSave} = props;
  const [editing, setEditing] = React.useState(false);
  const [conditions, setConditions] = React.useState(medicalHistory.conditions.blob);
  const [medications, setMedications] = React.useState(medicalHistory.medications.blob);
  const [mouseOver, setMouseOver] = React.useState(false);

  React.useEffect(() => {
    if (editing) return;
    setConditions(medicalHistory?.conditions?.blob || '');
    setMedications(medicalHistory?.medications?.blob || '');
  }, [medicalHistory, editing]);

  const handleSave = () => {
    const updatedSection = {
      medicalHistory: {
        conditions: {
          blob: conditions,
          editorType: 'MARKDOWN',
        },
        medications: {
          blob: medications,
          editorType: 'MARKDOWN',
        },
      },
    };
    onSave(updatedSection);
    setEditing(false);
  };

  const handleCancel = () => {
    setConditions(medicalHistory.conditions.blob);
    setMedications(medicalHistory.medications.blob);
    setEditing(false);
  };

  return (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
      }}
      onMouseOver={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          backgroundColor: 'grey.100',
          justifyContent: 'space-between',
          borderRadius: '4px 4px 0 0',
          width: '100%',
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <MedicalInformationOutlinedIcon sx={{mr: 1}} />
          <Typography variant="h6">
            <Translate expression="client.ClientDetail.Careplan.MedicalHistory.Title" />
          </Typography>
        </Box>
        <IconButton
          sx={{
            visibility: editing ? 'hidden' : 'visible',
            opacity: mouseOver ? 1 : 0,
            transition: 'opacity 0.2s',
          }}
          onClick={() => setEditing(true)}
        >
          <EditIcon/>
        </IconButton>
      </Box>
      <Box sx={{p: 2}}>
        {
          editing ? (
            <>
              <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 'bold'}}>
                <Translate expression="client.ClientDetail.Careplan.MedicalHistory.Conditions" />
              </Typography>
              <MarkdownEditor
                value={conditions}
                onChange={setConditions}
              />
              <Typography variant="subtitle1" sx={{mt: 2, mb: 1, fontWeight: 'bold'}}>
                <Translate expression="client.ClientDetail.Careplan.MedicalHistory.Medications" />
              </Typography>
              <MarkdownEditor
                value={medications}
                onChange={setMedications}
              />
              <Stack direction="row" justifyContent="flex-end" spacing={1} pt={1}>
                <Button onClick={handleCancel}>
                  <Translate expression="generic.Cancel" />
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  sx={{minWidth: '100px'}}
                >
                  <Translate expression="generic.Save" />
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 'bold'}}>
                <Translate expression="client.ClientDetail.Careplan.MedicalHistory.Conditions" />
              </Typography>
              <MarkdownViewer
                value={conditions || translate('client.ClientDetail.Careplan.MedicalHistory.NoConditions')}
                truncate={true}
              />
              <Typography variant="subtitle1" sx={{mt: 2, mb: 1, fontWeight: 'bold'}}>
                <Translate expression="client.ClientDetail.Careplan.MedicalHistory.Medications" />
              </Typography>
              <MarkdownViewer
                value={medications || translate('client.ClientDetail.Careplan.MedicalHistory.NoMedications')}
                truncate={true}
              />
            </>
          )
        }
      </Box>
    </Paper>
  );
};

MedicalHistory.propTypes = {
  medicalHistory: PropTypes.shape({
    conditions: PropTypes.shape({
      blob: PropTypes.string,
      editorType: PropTypes.string,
    }),
    medications: PropTypes.shape({
      blob: PropTypes.string,
      editorType: PropTypes.string,
    }),
  }).isRequired,
  onSave: PropTypes.func.isRequired,
};

export default MedicalHistory;
